import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  X,
  Settings,
  Palette,
  Layout,
  Maximize,
  Volume2,
  VolumeX,
  Bell,
  BellOff,
  Eye,
  Save,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import {
  CHARACTER_CATEGORIES,
  ART_STYLES,
  OFFICE_ENVIRONMENTS,
  OFFICE_BACKGROUNDS,
} from './themes';

// Default settings
export const DEFAULT_OFFICE_SETTINGS = {
  style: 'modern',
  layout: 'grid',
  agentSize: 'medium',
  showActivityFeed: true,
  showStats: true,
  soundEnabled: false,
  notificationsEnabled: true,
  eventInterval: 8000,
  backgroundColor: 'default',
  showAccessories: true,
};

const LAYOUT_OPTIONS = [
  { id: 'grid', name: 'Grid', icon: '⊞' },
  { id: 'row', name: 'Row', icon: '≡' },
  { id: 'circle', name: 'Circle', icon: '○' },
];

const SIZE_OPTIONS = [
  { id: 'small', name: 'Small' },
  { id: 'medium', name: 'Medium' },
  { id: 'large', name: 'Large' },
];

/**
 * OfficeSettings - Modal for configuring office appearance
 * Now supports changing themes/styles without recreating
 */
const OfficeSettings = ({
  isOpen,
  onClose,
  settings,
  theme,
  onSave,
}) => {
  const [localSettings, setLocalSettings] = useState(settings || DEFAULT_OFFICE_SETTINGS);
  const [localTheme, setLocalTheme] = useState(theme || { category: 'robots', artStyle: 'modern', environment: 'corporate' });
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState('style'); // style, layout, toggles

  useEffect(() => {
    if (settings) setLocalSettings(settings);
    if (theme) setLocalTheme(theme);
  }, [settings, theme]);

  useEffect(() => {
    const settingsChanged = JSON.stringify(localSettings) !== JSON.stringify(settings);
    const themeChanged = JSON.stringify(localTheme) !== JSON.stringify(theme);
    setHasChanges(settingsChanged || themeChanged);
  }, [localSettings, localTheme, settings, theme]);

  const updateSetting = (key, value) => {
    console.log('updateSetting called:', key, value);
    setLocalSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      console.log('New settings:', newSettings);
      return newSettings;
    });
  };

  const updateTheme = (key, value) => {
    console.log('updateTheme called:', key, value);
    setLocalTheme(prev => {
      const newTheme = { ...prev, [key]: value };
      console.log('New theme:', newTheme);
      return newTheme;
    });
    // Also update the artStyle in settings to match
    if (key === 'artStyle') {
      setLocalSettings(prev => ({ ...prev, style: value }));
    }
  };

  const handleSave = () => {
    console.log('handleSave called with:', localSettings, localTheme);
    onSave(localSettings, localTheme);
    onClose();
  };

  const handleReset = () => {
    setLocalSettings(DEFAULT_OFFICE_SETTINGS);
    setLocalTheme({ category: 'robots', artStyle: 'modern', environment: 'corporate' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm z-0" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-2xl max-h-[85vh] overflow-hidden bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl animate-scale-in flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-[#ff4d4d]" />
            <h2 className="text-lg font-semibold text-white">Office Settings</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="px-6 py-2 border-b border-white/10 flex gap-2 flex-shrink-0">
          {[
            { id: 'style', label: 'Style & Theme', icon: Palette },
            { id: 'layout', label: 'Layout', icon: Layout },
            { id: 'toggles', label: 'Options', icon: Settings },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#ff4d4d] text-white'
                  : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Style & Theme Tab */}
          {activeTab === 'style' && (
            <div className="space-y-6">
              {/* Art Style - THE MAIN VISUAL CHANGE */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-[#ff4d4d]" />
                  <h3 className="text-sm font-semibold text-white">Art Style</h3>
                  <span className="text-xs text-white/40">(Changes visual appearance)</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {Object.values(ART_STYLES).map((style) => (
                    <button
                      key={style.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateTheme('artStyle', style.id);
                      }}
                      className={`p-3 rounded-xl border-2 transition-all text-center cursor-pointer active:scale-95 ${
                        localTheme.artStyle === style.id
                          ? 'border-[#ff4d4d] bg-[#ff4d4d]/10 ring-2 ring-[#ff4d4d]/50'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <div className={`w-full h-8 rounded-lg mb-2 ${style.preview}`} />
                      <p className="text-xs text-white font-medium">{style.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Character Category */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-white">Character Category</h3>
                  <span className="text-xs text-white/40">(Preview only - agents keep their characters)</span>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {Object.values(CHARACTER_CATEGORIES).map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateTheme('category', cat.id);
                      }}
                      className={`p-2 rounded-xl border-2 transition-all text-center cursor-pointer active:scale-95 ${
                        localTheme.category === cat.id
                          ? 'border-[#ff4d4d] bg-[#ff4d4d]/10 ring-2 ring-[#ff4d4d]/50'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <span className="text-2xl">{cat.icon}</span>
                      <p className="text-[10px] text-white mt-1">{cat.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Environment */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Office Environment</h3>
                <div className="grid grid-cols-4 gap-2">
                  {Object.values(OFFICE_ENVIRONMENTS).map((env) => (
                    <button
                      key={env.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateTheme('environment', env.id);
                      }}
                      className={`p-2 rounded-xl border-2 transition-all text-center cursor-pointer active:scale-95 ${
                        localTheme.environment === env.id
                          ? 'border-[#ff4d4d] bg-[#ff4d4d]/10 ring-2 ring-[#ff4d4d]/50'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <span className="text-xl">{env.icon}</span>
                      <p className="text-[10px] text-white mt-1">{env.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Background */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Background</h3>
                <div className="grid grid-cols-5 gap-2">
                  {Object.values(OFFICE_BACKGROUNDS).map((bg) => (
                    <button
                      key={bg.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateSetting('backgroundColor', bg.id);
                      }}
                      className={`p-2 rounded-xl border-2 transition-all cursor-pointer active:scale-95 ${
                        localSettings.backgroundColor === bg.id
                          ? 'border-[#ff4d4d] ring-2 ring-[#ff4d4d]/50'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <div className={`w-full h-8 rounded-lg mb-1 ${bg.className}`} />
                      <p className="text-[10px] text-white text-center">{bg.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Layout Tab */}
          {activeTab === 'layout' && (
            <div className="space-y-6">
              {/* Layout */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Agent Layout</h3>
                <div className="flex gap-3">
                  {LAYOUT_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateSetting('layout', option.id);
                      }}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all text-center cursor-pointer active:scale-95 ${
                        localSettings.layout === option.id
                          ? 'border-[#ff4d4d] bg-[#ff4d4d]/10 ring-2 ring-[#ff4d4d]/50'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <span className="text-2xl">{option.icon}</span>
                      <p className="text-sm text-white mt-2">{option.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Agent Size */}
              <div>
                <h3 className="text-sm font-semibold text-white mb-3">Agent Size</h3>
                <div className="flex gap-3">
                  {SIZE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        updateSetting('agentSize', option.id);
                      }}
                      className={`flex-1 p-3 rounded-xl border-2 transition-all text-center cursor-pointer active:scale-95 ${
                        localSettings.agentSize === option.id
                          ? 'border-[#ff4d4d] bg-[#ff4d4d]/10 ring-2 ring-[#ff4d4d]/50'
                          : 'border-white/10 hover:border-white/30 hover:bg-white/5'
                      }`}
                    >
                      <p className="text-sm text-white font-medium">{option.name}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Toggles Tab */}
          {activeTab === 'toggles' && (
            <div className="space-y-3">
              {/* Activity Feed */}
              <ToggleOption
                icon={Bell}
                label="Activity Feed"
                description="Show real-time event feed"
                value={localSettings.showActivityFeed}
                onChange={(v) => updateSetting('showActivityFeed', v)}
              />

              {/* Stats */}
              <ToggleOption
                icon={Eye}
                label="Statistics Panel"
                description="Display agent performance stats"
                value={localSettings.showStats}
                onChange={(v) => updateSetting('showStats', v)}
              />

              {/* Desk Accessories */}
              <ToggleOption
                icon={Sparkles}
                label="Desk Accessories"
                description="Show items on agent desks"
                value={localSettings.showAccessories}
                onChange={(v) => updateSetting('showAccessories', v)}
              />

              {/* Sound */}
              <ToggleOption
                icon={localSettings.soundEnabled ? Volume2 : VolumeX}
                label="Sound Effects"
                description="Play sounds for new events"
                value={localSettings.soundEnabled}
                onChange={(v) => updateSetting('soundEnabled', v)}
              />

              {/* Notifications */}
              <ToggleOption
                icon={localSettings.notificationsEnabled ? Bell : BellOff}
                label="Desktop Notifications"
                description="Notify when agents receive messages"
                value={localSettings.notificationsEnabled}
                onChange={(v) => updateSetting('notificationsEnabled', v)}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between flex-shrink-0">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Save button clicked, hasChanges:', hasChanges);
                handleSave();
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                hasChanges
                  ? 'bg-[#ff4d4d] text-white hover:bg-[#ff6666]'
                  : 'bg-[#ff4d4d]/50 text-white/70 hover:bg-[#ff4d4d]/70'
              }`}
            >
              <Save className="w-4 h-4" />
              {hasChanges ? 'Save Changes' : 'Save (No Changes)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toggle option component
const ToggleOption = ({ icon: Icon, label, description, value, onChange }) => (
  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-white/60" />
      <div>
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-white/50">{description}</p>
      </div>
    </div>
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(!value);
      }}
      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${value ? 'bg-[#ff4d4d]' : 'bg-white/20'}`}
    >
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow-md ${value ? 'right-1' : 'left-1'}`} />
    </button>
  </div>
);

OfficeSettings.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  settings: PropTypes.object,
  theme: PropTypes.shape({
    category: PropTypes.string,
    artStyle: PropTypes.string,
    environment: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
};

export default OfficeSettings;
